import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { RESERVED_PATHS } from '../utils/reservedPaths';
import { hasPaidAccess } from '../utils/entitlements';
import { useAuth } from '../contexts/AuthContext';
import '../styles/design-system.css';
import '../styles/navbar.css';

export default function LabNavbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [publicProfileDropdown, setPublicProfileDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  
  const { user, userProfile, logOut } = useAuth();
  const isPro = hasPaidAccess(userProfile);

  const handleLogout = async () => {
    try {
      await logOut();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Do not auto-collapse on outside clicks; keep menu stable during page interactions.
  
  // ── Menu structure ──
  // All groups visible to everyone. Sub-items with requiresAuth redirect guests to login on click.
  // Groups with authOnly: true are hidden entirely from guests.
  const navGroups = [
    { id: 'recipes', label: 'Recipes', items: [
      { path: '/calculator', label: 'Lye Calculator' },
      { path: '/melt-and-pour', label: 'Melt & Pour' },
      { path: '/recipes', label: 'My Recipes', requiresAuth: true },
      { path: '/recipes/public', label: 'Recipe Collection' },
      { path: '/generator', label: 'Recipe Generator', requiresAuth: true, requiresPro: true },
      { path: '/templates', label: 'Templates' },
    ]},
    { id: 'batches', label: 'Batches', items: [
      // { path: '/batches/new', label: 'New Batch', requiresAuth: true },
      { path: '/batches', label: 'My Batches', requiresAuth: true },
      { path: '/quickbatch', label: 'Quick Batch', requiresAuth: true, requiresPro: true },
      { path: '/calendar', label: 'Calendar', requiresAuth: true },
    ]},
    { id: 'inventory', label: 'Inventory', items: [
      { path: '/inventory', label: 'Ingredients', requiresAuth: true },
      { path: '/inventory/purchase', label: 'Add Purchase', requiresAuth: true },
      { path: '/inventory/purchases', label: 'My Purchases', requiresAuth: true },
      { path: '/inventory/suppliers', label: 'Suppliers', requiresAuth: true },
      { path: '/inventory/packaging', label: 'Packaging', requiresAuth: true },
    ]},
    { id: 'sales', label: 'Sales', items: [
      { path: '/sales', label: 'Sales', requiresAuth: true, requiresPro: true },
      { path: '/reports', label: 'Analytics', requiresAuth: true, requiresPro: true },
    ]},
    { id: 'utility', label: 'Utility', items: [
      // { path: '/notes', label: 'Notes', requiresAuth: true },
      { path: '/labels', label: 'Labels', requiresAuth: true, requiresPro: true },
      { path: '/print', label: 'Print Center', requiresAuth: true, requiresPro: true },
    ]},
    { id: 'account', label: 'Account', items: [
      { path: '/profile', label: 'Profile', requiresAuth: true },
      { path: '/settings', label: 'Settings', requiresAuth: true },
      { path: '/backup', label: 'Backup', requiresAuth: true, requiresPro: true },
      ...(user ? [{ path: '#logout', label: 'Log Out', isAction: true }] : []),
    ]},
    { id: 'community', label: 'Community', items: [
      { path: '/community', label: 'Forum' },
      { path: '/feature-requests', label: 'Feature Requests' },
    ]},
    { id: 'blog', label: 'Blog', path: '/blog', isLink: true },
    ...(!user ? [{ id: 'auth', label: 'Login / Sign up', path: '/login', isLink: true }] : []),
  ];

  const bottomLinks = [
    { id: 'help', label: 'Help Center', path: '/help' },
    { id: 'contact', label: 'Contact Us', path: '/contact' },
  ];

  const filteredNavGroups = navGroups;

  const pathMatches = (base, current) =>
    current === base || (base !== '/' && current.startsWith(`${base}/`));

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const isUsernameProfile = pathSegments.length === 1 && !RESERVED_PATHS.has(pathSegments[0]);
  const isPublicProfile = (location.pathname.startsWith('/profile/') && location.pathname !== '/profile') || isUsernameProfile;

  // Routes that should NOT activate any nav group
  const neutralPaths = ['/contact', '/help'];
  const isNeutralRoute = neutralPaths.some(p => location.pathname.startsWith(p));

  const allItemPaths = useMemo(() => {
    const paths = new Set();
    navGroups.forEach(g => {
      if (g.isLink) paths.add(g.path);
      else g.items?.forEach(i => paths.add(i.path));
    });
    return paths;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const isActive = (item) => {
    if (isNeutralRoute) return false;
    if (isPublicProfile && item.path === '/community') return true;
    if (isPublicProfile && item.path === '/profile') return false;
    // Exact match always wins
    if (location.pathname === item.path) return true;
    // Prefix match only if no other nav item is an exact match for the current path
    if (item.path !== '/' && location.pathname.startsWith(`${item.path}/`)) {
      return !allItemPaths.has(location.pathname);
    }
    return false;
  };

  const isGroupActive = (group) => {
    if (isNeutralRoute) return false;
    if (isPublicProfile && group.id === 'community') return true;
    return group.isLink
      ? pathMatches(group.path, location.pathname)
      : group.items?.some(item => isActive(item));
  };
  const activeGroupId = useMemo(() => {
    const activeGroup = filteredNavGroups.find((group) => !group.isLink && isGroupActive(group));
    return activeGroup ? activeGroup.id : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isPublicProfile, isNeutralRoute, user]);
  const dropdownRefs = useRef({});
  const displayedOpenDropdown = isPublicProfile
    ? (publicProfileDropdown ?? 'community')
    : openDropdown;

  const toggleDropdown = useCallback((groupId) => {
    if (isPublicProfile) {
      setPublicProfileDropdown(prev => prev === groupId ? null : groupId);
      return;
    }
    setOpenDropdown(prev => prev === groupId ? null : groupId);
  }, [isPublicProfile]);

  const keepDropdownOpen = useCallback((groupId) => {
    if (isPublicProfile) {
      setPublicProfileDropdown(groupId);
      return;
    }
    setOpenDropdown(groupId);
  }, [isPublicProfile]);

  useEffect(() => {
    setMobileMenuOpen(false);
    if (!isPublicProfile) {
      setPublicProfileDropdown(null);
    }
  }, [location.pathname, isPublicProfile]);
  useEffect(() => {
    if (isPublicProfile) {
      setOpenDropdown('community');
      return;
    }
    // Always sync openDropdown to the active group on route changes
    setOpenDropdown(activeGroupId);
  }, [location.pathname, isPublicProfile, activeGroupId]);

  const handleItemClick = (item) => {
    if (item.isAction && item.label === 'Log Out') {
      handleLogout();
      return;
    }
    if (item.requiresAuth && !user) {
      navigate('/login', { state: { from: location } });
    }
  };

  return (
    <nav className="sl-navbar">
      <div className="sl-navbar-inner">
        <Link to="/" className="sl-navbar-brand borderbottom">
          <img src="/Images/sllogoblack.png" alt="SoapLab Logo" />
        </Link>

        <div className="sl-navbar-nav borderbottom" ref={dropdownRef}>
          {filteredNavGroups.map(group => (
            <div key={group.id} className="sl-nav-item">
              {group.isLink ? (
                <Link to={group.path} className={`sl-nav-link ${isGroupActive(group) ? 'active' : ''} ${group.id === 'auth' ? 'sl-login-link' : ''}`}>
                  <span>{group.label}</span>
                </Link>
              ) : (
                <>
                  <button 
                    className={`sl-nav-link ${isGroupActive(group) ? 'parent-active' : ''} ${displayedOpenDropdown === group.id ? 'open' : ''}`}
                    onClick={() => toggleDropdown(group.id)}
                  >
                    <span>{group.label}</span>
                    <span className="sl-nav-arrow">▾</span>
                  </button>
                  <div 
                    className={`sl-nav-dropdown ${displayedOpenDropdown === group.id ? 'open' : ''}`}
                    ref={el => dropdownRefs.current[group.id] = el}
                  >
                    {group.items.map(item => (
                      item.isAction ? (
                        <button
                          key={item.path}
                          className="sl-dropdown-item sl-dropdown-action"
                          onClick={() => handleItemClick(item)}
                        >
                          <span>{item.label}</span>
                        </button>
                      ) : (
                        <Link 
                          key={item.path}
                          to={item.requiresAuth && !user ? '#' : item.path}
                          className={`sl-dropdown-item ${isActive(item) ? 'active' : ''}`}
                          onClick={(e) => {
                            if (item.requiresAuth && !user) {
                              e.preventDefault();
                              handleItemClick(item);
                              return;
                            }
                            keepDropdownOpen(group.id);
                          }}
                        >
                          <span>{item.label}</span>
                          {item.requiresPro && !isPro && <span className="sl-pro-badge">PRO</span>}
                          {item.requiresAuth && !user && <span className="sl-login-badge">LOGIN</span>}
                        </Link>
                      )
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Help Center & Contact Us */}
        <div className="sl-navbar-bottom">
          {bottomLinks.map(link => (
            <Link
              key={link.id}
              to={link.path}
              className={`sl-bottom-link ${location.pathname === link.path ? 'active' : ''}`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <button className="sl-mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
          <span className="sl-mobile-toggle-text">{mobileMenuOpen ? 'Close' : 'Menu'}</span>
          <span className={`sl-hamburger ${mobileMenuOpen ? 'open' : ''}`}>
            <span></span><span></span><span></span>
          </span>
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`sl-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        {filteredNavGroups.map(group => (
          <div key={group.id} className="sl-mobile-nav-group">
            {group.isLink ? (
              <Link to={group.path} className={`sl-mobile-nav-link ${isGroupActive(group) ? 'active' : ''} ${group.id === 'auth' ? 'sl-mobile-login-link' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                <span>{group.label}</span>
              </Link>
            ) : (
              <>
                <div className="sl-mobile-nav-header"><span>{group.label}</span></div>
                {group.items.map(item => (
                  item.isAction ? (
                    <button key={item.path} className="sl-mobile-nav-link sl-mobile-action" onClick={() => { handleItemClick(item); setMobileMenuOpen(false); }}>
                      <span>{item.label}</span>
                    </button>
                  ) : (
                    <Link 
                      key={item.path} 
                      to={item.requiresAuth && !user ? '#' : item.path} 
                      className={`sl-mobile-nav-link ${isActive(item) ? 'active' : ''}`} 
                      onClick={(e) => {
                        if (item.requiresAuth && !user) {
                          e.preventDefault();
                          navigate('/login', { state: { from: location } });
                        }
                        setMobileMenuOpen(false);
                      }}
                    >
                      <span>{item.label}</span>
                      {item.requiresPro && !isPro && <span className="sl-pro-badge">PRO</span>}
                      {item.requiresAuth && !user && <span className="sl-login-badge">LOGIN</span>}
                    </Link>
                  )
                ))}
              </>
            )}
          </div>
        ))}
        <div className="sl-mobile-bottom-links">
          {bottomLinks.map(link => (
            <Link
              key={link.id}
              to={link.path}
              className={`sl-mobile-nav-link sl-mobile-bottom-link ${location.pathname === link.path ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <span>{link.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
}
